/**
 * ============================================================================
 * DASHBOARD SLICE — global UI state: mode, week selection, filters.
 * ============================================================================
 *
 * PERSISTENCE: Stored to localStorage (versioned key). On reload, selections
 * restore — user doesn't lose their Live/Snapshot mode or chosen week.
 *
 * FILTERS: Live in Redux, applied client-side via Arquero. NOT part of the
 * RTK Query cache key — filter changes are instant, no network call.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DashboardFilters, EMPTY_FILTERS, NetworkId, WeekWindow } from 'src/types';
import { DEFAULT_FILTER_QUERY } from 'src/config/filterConfig';
import { DEFAULT_SELECTED_DEMAND_CATEGORIES } from 'src/config/demandCategories';
import { DEFAULT_LAST_X_WEEKS, DEFAULT_FORWARD_X_WEEKS } from 'src/features/api/durationRange';

// v4: metricGroup became an enforcing filter scoped to the volume visuals, and
// the default is a single WBR Metrics Only chip (v3 briefly persisted an extra
// 'OTD Metrics' chip) — version bump invalidates persisted
// v1 snapshots so every user picks up the new defaults once.
const STORAGE_KEY = 'wbr_dashboard_state_v4';

export interface FilterQuery {
  tokens: Array<{ propertyKey: string; operator: string; value: string }>;
  operation: 'and' | 'or';
}

export interface DashboardState {
  activeNetwork: NetworkId;
  activeView: string;
  weekWindow: WeekWindow;
  filters: DashboardFilters;
  filterQuery: FilterQuery;
  selectedNetworkDrilldown: NetworkId | null;
  dataMode: 'live' | 'snapshot';
  selectedWeek: string | null;
  /** Anchor week for the tables' window (null = backend current week). */
  reportingWeek: string | null;
  /** Weeks BEFORE the reporting week to fetch (QuickSight "Last X Historical Periods"). */
  lastXWeeks: number;
  /** Weeks AFTER the reporting week to fetch ("Next X Future Periods"). Snapshot forces 0. */
  forwardXWeeks: number;
  /** DEMAND_CATEGORIES whose section groups render on the Detailed Summary tab (QuickSight checkbox row). */
  selectedDemandCategories: NetworkId[];
  /** "All Networks - On Time Delivery %" rollup section toggle. */
  showAllNetworksOtd: boolean;
}

/**
 * Week computation is NOT done client-side. The API returns week labels
 * (e.g. "2026 Week 29") and the UI uses them as-is. These helpers exist
 * only as fallbacks for initial Redux state before API data loads.
 * Once data loads, HomePage's useEffect syncs to the actual API weeks.
 */
export function getDefaultWeek(_mode: 'live' | 'snapshot'): string {
  return '';
}

// ──── localStorage persistence (versioned, with schema guard) ────

interface PersistedFields {
  activeNetwork?: NetworkId;
  activeView?: string;
  weekWindow?: WeekWindow;
  filters?: DashboardFilters;
  filterQuery?: FilterQuery;
  dataMode?: 'live' | 'snapshot';
  selectedWeek?: string | null;
  reportingWeek?: string | null;
  lastXWeeks?: number;
  forwardXWeeks?: number;
  selectedDemandCategories?: NetworkId[];
  showAllNetworksOtd?: boolean;
}

function loadPersisted(): PersistedFields {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as PersistedFields;
  } catch {
    return {};
  }
}

const persisted = loadPersisted();

const initialState: DashboardState = {
  activeNetwork: persisted.activeNetwork ?? 'all',
  activeView: persisted.activeView ?? 'detailed-summary',
  weekWindow: persisted.weekWindow ?? { from: '2026-W25', to: '2026-W29' },
  filters: persisted.filters ?? EMPTY_FILTERS,
  filterQuery: persisted.filterQuery ?? DEFAULT_FILTER_QUERY,
  selectedNetworkDrilldown: null,
  dataMode: persisted.dataMode ?? 'live',
  selectedWeek: persisted.selectedWeek ?? getDefaultWeek('live'),
  reportingWeek: persisted.reportingWeek ?? null,
  lastXWeeks: persisted.lastXWeeks ?? DEFAULT_LAST_X_WEEKS,
  forwardXWeeks: persisted.forwardXWeeks ?? DEFAULT_FORWARD_X_WEEKS,
  selectedDemandCategories: persisted.selectedDemandCategories ?? DEFAULT_SELECTED_DEMAND_CATEGORIES,
  showAllNetworksOtd: persisted.showAllNetworksOtd ?? false,
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    networkSelected(state, action: PayloadAction<NetworkId>) {
      state.activeNetwork = action.payload;
      state.selectedNetworkDrilldown = null;
    },
    viewSelected(state, action: PayloadAction<string>) {
      state.activeView = action.payload;
    },
    weekWindowChanged(state, action: PayloadAction<WeekWindow>) {
      state.weekWindow = action.payload;
    },
    filterChanged(state, action: PayloadAction<{ key: keyof DashboardFilters; value: DashboardFilters[keyof DashboardFilters] }>) {
      (state.filters as Record<string, unknown>)[action.payload.key] = action.payload.value;
    },
    filtersReplaced(state, action: PayloadAction<DashboardFilters>) {
      state.filters = action.payload;
    },

    /** "Clear filters" button — removes ALL active filters including defaults. */
    filtersCleared(state) {
      state.filters = {
        ...EMPTY_FILTERS,
        period: '',
        metricGroup: 'all',
      };
    },
    drilldownOpened(state, action: PayloadAction<NetworkId>) {
      state.selectedNetworkDrilldown = action.payload;
    },
    drilldownClosed(state) {
      state.selectedNetworkDrilldown = null;
    },
    filterQueryChanged(state, action: PayloadAction<FilterQuery>) {
      state.filterQuery = action.payload;
    },
    dataModeChanged(state, action: PayloadAction<'live' | 'snapshot'>) {
      state.dataMode = action.payload;
      // selectedWeek will be synced by HomePage's useEffect once API data loads
      state.selectedWeek = null;
      // Snapshot is a record of the past — no forward weeks (QuickSight parity).
      if (action.payload === 'snapshot') {
        state.forwardXWeeks = 0;
      } else if (state.forwardXWeeks === 0) {
        state.forwardXWeeks = DEFAULT_FORWARD_X_WEEKS;
      }
    },
    selectedWeekChanged(state, action: PayloadAction<string>) {
      state.selectedWeek = action.payload;
    },
    /** Reporting-week anchor for the tables (null = backend current week). */
    reportingWeekChanged(state, action: PayloadAction<string | null>) {
      state.reportingWeek = action.payload;
    },
    /** "Last X Historical Periods" — changes the fetch window (new API call). */
    lastXWeeksChanged(state, action: PayloadAction<number>) {
      state.lastXWeeks = action.payload;
    },
    /** "Forward X Periods" — live mode only; snapshot pins it to 0. */
    forwardXWeeksChanged(state, action: PayloadAction<number>) {
      state.forwardXWeeks = state.dataMode === 'snapshot' ? 0 : action.payload;
    },
    /** Replace the checkbox selection wholesale (tile drill-down, legacy /network/:id landing). */
    demandCategoriesReplaced(state, action: PayloadAction<NetworkId[]>) {
      state.selectedDemandCategories = action.payload;
    },
    /** Detailed Summary demand-category checkbox toggled (QuickSight parity row). */
    demandCategoryToggled(state, action: PayloadAction<NetworkId>) {
      const id = action.payload;
      state.selectedDemandCategories = state.selectedDemandCategories.includes(id)
        ? state.selectedDemandCategories.filter((n) => n !== id)
        : [...state.selectedDemandCategories, id];
    },
    /** "All Networks - On Time Delivery %" rollup checkbox. */
    allNetworksOtdToggled(state, action: PayloadAction<boolean>) {
      state.showAllNetworksOtd = action.payload;
    },
  },
});

export const {
  networkSelected,
  viewSelected,
  weekWindowChanged,
  filterChanged,
  filtersReplaced,
  filtersCleared,
  drilldownOpened,
  drilldownClosed,
  filterQueryChanged,
  dataModeChanged,
  selectedWeekChanged,
  reportingWeekChanged,
  lastXWeeksChanged,
  forwardXWeeksChanged,
  demandCategoriesReplaced,
  demandCategoryToggled,
  allNetworksOtdToggled,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;

/** Persist only on dashboard slice action dispatches (not RTK Query internals). */
let lastPersisted = '';
export function persistDashboardState(state: DashboardState): void {
  const toStore: PersistedFields = {
    activeNetwork: state.activeNetwork,
    activeView: state.activeView,
    weekWindow: state.weekWindow,
    filters: state.filters,
    filterQuery: state.filterQuery,
    dataMode: state.dataMode,
    selectedWeek: state.selectedWeek,
    reportingWeek: state.reportingWeek,
    lastXWeeks: state.lastXWeeks,
    forwardXWeeks: state.forwardXWeeks,
    selectedDemandCategories: state.selectedDemandCategories,
    showAllNetworksOtd: state.showAllNetworksOtd,
  };
  const json = JSON.stringify(toStore);
  if (json === lastPersisted) return; // skip unchanged writes
  lastPersisted = json;
  try {
    localStorage.setItem(STORAGE_KEY, json);
  } catch { /* quota exceeded */ }
}
